/**
 * Lead Beacon
 *
 * Opt-in (default OFF, v1.7.0): wires this plugin into go-grad's pay-per-lead
 * metering engine. When enabled, ONE delegated 'click'+'auxclick' listener is
 * bound at UtmLinker construction time — immune to MutationObserver re-scans,
 * unlike the per-link href-tagging pipeline in UtmLinker.js#processLinks(),
 * which re-runs on every DOM mutation. On a click/tap that resolves against
 * the admin-configured attribution map, a fire-and-forget beacon is POSTed to
 * go-grad's `POST /ingest/outbound` (internal/adstore/ingest.go is the wire
 * contract's authority — keep BOTH sides in sync).
 *
 * This module owns NO tagging logic of its own and never touches UtmLinker's
 * href-rewrite internals (boundaries F4a) — it only reads the SAME anchor
 * element the tagging pipeline already processed, resolves whether it's a
 * metered partner link, and fires the beacon. Host matching REUSES
 * LinkUtils.matchDomainPattern() + the exported LinkUtils.normalizeHostname()
 * (the SAME matcher/normalizer excludeDomains/click-id-passthrough already
 * use) rather than a second implementation (anti-reinvention F2 / boundaries
 * F4b) — `hostMatchesForBilling()` below is a thin, DOCUMENTED divergence in
 * semantics only (exact-unless-wildcard, for billing), not a duplicated
 * matcher.
 *
 * daily_id here is DELIBERATELY DISTINCT from piter-now's `pn:did` identity
 * (apps/piter/src/lib/identity.ts) — different era, different product, no
 * cross-system reconciliation. go-grad's ingest.go only requires the SHAPE
 * (64 lowercase hex chars), not a shared identity space with the Astro site.
 */

import { matchDomainPattern, normalizeHostname } from './LinkUtils.js';

/**
 * go-grad's fixed ingest path (internal/adstore/ingest.go) — not a setting
 * (pragmatist cut #2, BUILD-PLAN T1). REQUIRED DEPLOYMENT CONTRACT
 * (pr-review-council HIGH #4): this is a SAME-ORIGIN relative path — the WP
 * box's edge (nginx, piteronline.tv.conf) MUST reverse-proxy
 * `/ingest/outbound` to go-grad (see DESIGN.md "Lead Beacon" ->
 * "Deployment" and the BUILD-PLAN's T3). Without that proxy rule, every
 * fire 404s HARMLESSLY — `sendBeacon`/`fetch` both swallow the response in
 * production mode (see fireLeadBeacon() below), so a missing proxy fails
 * SILENT, not loud. Enabling `leadBeacon` on a site whose edge isn't
 * provisioned for this route is a real but invisible no-op; verify with
 * debugMode on (reads and logs the response status) before relying on it.
 *
 * THREAT MODEL (pr-review-council HIGH #4, re-gate round 2 — see DESIGN.md
 * "Lead Beacon" -> "Threat model" for the full writeup): this payload is
 * INTENTIONALLY UNAUTHENTICATED. No client-side HMAC/signing was added —
 * any secret shipped to the browser via `wp_localize_script` is public by
 * definition, so signing in an attacker-controlled runtime buys zero real
 * authenticity. Forged/replayed beacons are IN-MODEL; the real controls are
 * server-side + operational (go-grad velocity windows, GIVT bot filter,
 * dedup, a mandatory `monthly_cap>0` at provisioning, nginx `limit_req` at
 * the edge, and a one-partner pilot with invoice-time reconciliation).
 */
export const LEAD_BEACON_ENDPOINT = '/ingest/outbound';

/** Fixed `source` discriminator (WP vs a future Astro source) — not a setting. */
export const LEAD_BEACON_SOURCE = 'wp-beacon';

/** daily_id shape go-grad's ingest.go requires: 64 lowercase hex chars. */
export const DAILY_ID_RE = /^[0-9a-f]{64}$/;

/**
 * Example attribution-map fixture shown in the admin help text
 * (admin/pages/LeadBeaconPanel.jsx imports THIS constant rather than a
 * hand-copied duplicate). Kept intentionally small (Map-size fitness,
 * anti-reinvention F3 / boundaries F3): tests/lead-beacon.test.mjs asserts
 * this never grows past 3 entries — more than a couple of partners is a
 * signal this site has outgrown the map and should render `data-ad-id`
 * directly on its own partner markup instead (see LeadBeaconPanel.jsx's
 * migration notice, shown once the LIVE config passes 1 entry).
 *
 * @type {Array<{ad_id: number, hosts: string[], tels: string[]}>}
 */
export const EXAMPLE_ATTRIBUTION_FIXTURE = [
    { ad_id: 42, hosts: ['partner-clinic.example'], tels: ['+78125550100'] },
];

const DAILY_ID_STORAGE_KEY_PREFIX = 'utm_linker_lead_beacon_daily_id_';

// In-memory per-page fallback for when localStorage is unavailable/blocked
// (private browsing, quota exceeded, disabled by the user/policy). "More-
// distinct = safe direction": a fresh id per page load never OVER-counts a
// single visitor's leads against the wrong bucket — go-grad's own
// source_event_id idempotency key already guards against a single fire being
// double-counted, so under-deduping here (worst case) is the safe failure.
let inMemoryDailyId = null;
let inMemoryDailyIdDateKey = null;

// Same-GESTURE dedup (not same-SESSION): click+auxclick firing for one
// physical user action on the same anchor must beacon exactly once. Ported
// idiom (a plain Set, not a hand-rolled timer/debounce) from piter-now's
// interactions-client.ts `_seenViews`/`_seenShares` module-level Sets — but
// unlike those (permanent for the page's lifetime, by design, for
// once-per-session counters), this Set is cleared on the next microtask so a
// LATER, genuinely separate click on the same link still beacons again. A
// microtask (Promise.resolve().then) is used to clear it — deliberately NOT
// a timer (setTimeout/setInterval), matching the "not a hand-rolled timer"
// constraint while still draining synchronously-paired events (click then
// auxclick, or vice versa, dispatched in the same browser task).
const _firedThisGesture = new Set();
let _gestureClearScheduled = false;

function markGestureFired(anchor) {
    _firedThisGesture.add(anchor);
    if (_gestureClearScheduled) return;
    _gestureClearScheduled = true;
    Promise.resolve().then(() => {
        _firedThisGesture.clear();
        _gestureClearScheduled = false;
    });
}

function hasGestureFired(anchor) {
    return _firedThisGesture.has(anchor);
}

/**
 * TEST-ONLY: reset all module-level state (daily_id cache + gesture-dedup
 * Set) between test cases. Never called from production code — the `__test`
 * prefix (pr-review-council LOW #15, re-gate round 2) makes that scope
 * unambiguous at every call site, vs. a bare leading underscore that reads
 * as "private" rather than "test harness escape hatch".
 */
export function __testResetLeadBeaconState() {
    inMemoryDailyId = null;
    inMemoryDailyIdDateKey = null;
    _firedThisGesture.clear();
    _gestureClearScheduled = false;
}

/**
 * UTC calendar-day key, e.g. "2026-07-02". `Date#toISOString()` is always
 * UTC regardless of the runtime's local timezone.
 *
 * @param {Date} date
 * @return {string}
 */
function getUtcDateKey(date) {
    return date.toISOString().slice(0, 10);
}

/**
 * 32 cryptographically-random bytes, hex-encoded (64 chars) — the shape
 * go-grad's `dailyIDRE` requires. Returns null when no CSPRNG is available
 * (SSR, or an insecure-context WebView missing `crypto.getRandomValues`) —
 * same bail idiom as piter-now's identity.ts `getDeviceId()`.
 *
 * @return {?string}
 */
function generateHexDailyId() {
    if (typeof crypto === 'undefined' || typeof crypto.getRandomValues !== 'function') return null;

    const bytes = new Uint8Array(32);
    crypto.getRandomValues(bytes);

    let hex = '';
    for (const byte of bytes) hex += byte.toString(16).padStart(2, '0');
    return hex;
}

/**
 * Resolve (and persist) today's daily_id: a fresh random 64-hex value minted
 * once per UTC calendar day, stored under a DATE-suffixed localStorage key
 * so a day rollover naturally reads as "nothing stored yet" (no explicit
 * cleanup of yesterday's key needed — it's simply never read again).
 * Falls back to an in-memory value, stable for the rest of THIS page load,
 * when storage throws (private mode / quota / disabled).
 *
 * `date` is an injectable seam for tests (UTC-day rotation); production
 * callers never pass it.
 *
 * @param {Date} [date] Defaults to `new Date()`.
 * @return {?string} 64 lowercase hex chars, or null if no CSPRNG is available.
 */
export function getDailyId(date = new Date()) {
    const dateKey = getUtcDateKey(date);
    const storageKey = DAILY_ID_STORAGE_KEY_PREFIX + dateKey;

    try {
        if (typeof localStorage !== 'undefined' && localStorage !== null) {
            const stored = localStorage.getItem(storageKey);
            if (stored && DAILY_ID_RE.test(stored)) return stored;

            const fresh = generateHexDailyId();
            if (!fresh) return null;

            localStorage.setItem(storageKey, fresh);
            return fresh;
        }
    } catch (e) {
        // Storage blocked — fall through to the in-memory fallback below.
    }

    if (inMemoryDailyId && inMemoryDailyIdDateKey === dateKey) {
        return inMemoryDailyId;
    }

    const fresh = generateHexDailyId();
    if (!fresh) return null;

    inMemoryDailyId = fresh;
    inMemoryDailyIdDateKey = dateKey;
    return fresh;
}

/**
 * Normalize a phone-ish string to its last 10 digits — bridges the RU
 * intl-vs-domestic dialing prefix (`+7...` vs `8...`) and any admin-typed
 * punctuation (spaces/dashes/parens), so a config entry and a page's `tel:`
 * href can be written in different, equally-valid formats and still match.
 * Returns null when fewer than 10 digits are present (too short to be a
 * real subscriber number — never a false match).
 *
 * @param {string} raw
 * @return {?string}
 */
export function normalizeTelDigits(raw) {
    if (typeof raw !== 'string') return null;
    const digits = raw.replace(/\D/g, '');
    if (digits.length < 10) return null;
    return digits.slice(-10);
}

/**
 * Walk from `el` up through `parentElement` (stopping at, and excluding,
 * `document.body` — same bound as UtmGenerator.js's `determineUtmContent()`
 * banner-class ancestor walk) looking for the nearest enclosing `<a>`. The
 * click target can be any descendant of the actual link (an icon, a text
 * span), so the delegated listener needs this before it can resolve
 * attribution at all.
 *
 * @param {*} el
 * @return {*} The nearest ancestor anchor, or null.
 */
function findAncestorAnchor(el) {
    let node = el;
    while (node && node !== document.body) {
        if (node.tagName === 'A' && typeof node.href === 'string') return node;
        node = node.parentElement || null;
    }
    return null;
}

/**
 * Walk from `anchor` up through `parentElement` (same bound as
 * findAncestorAnchor()) looking for a `data-ad-id` override — checked on the
 * anchor itself first, then its ancestors. Wins over every other resolution
 * source (attribution-resolver order, step 1).
 *
 * @param {*} anchor
 * @return {?number} A positive ad_id, or null when none is found.
 */
function findDataAdId(anchor) {
    let node = anchor;
    while (node && node !== document.body) {
        const raw = node.dataset && node.dataset.adId;
        if (raw !== undefined && raw !== null && raw !== '') {
            const parsed = parseInt(raw, 10);
            if (Number.isInteger(parsed) && parsed > 0) return parsed;
        }
        node = node.parentElement || null;
    }
    return null;
}

/**
 * Billing-grade hostname match — DIVERGES from matchDomainPattern()'s own
 * semantics on purpose (pr-review-council MEDIUM #7). matchDomainPattern()
 * treats a BARE pattern (no `*.` prefix) as an implicit SUFFIX match too —
 * `"example.com"` also matches `"evil.example.com"` — which is the right,
 * safer-by-default call for tagging/exclusion (matching MORE is the safe
 * direction there: an over-matched excludeDomains entry just skips a link
 * that maybe didn't need skipping). For BILLING that direction is inverted:
 * matching MORE means invoicing a partner for a subdomain nobody agreed to
 * pay for. So here a bare pattern is EXACT-only (same lowercase +
 * `www.`-strip normalization matchDomainPattern uses internally, via the
 * now-exported LinkUtils.normalizeHostname — no second normalizer); an
 * admin who genuinely wants every subdomain billed must say so explicitly
 * with a `*.` prefix, which delegates to matchDomainPattern() unchanged.
 *
 * @param {string} hostname
 * @param {string} pattern
 * @return {boolean}
 */
function hostMatchesForBilling(hostname, pattern) {
    if (!hostname || !pattern) return false;
    if (pattern.startsWith('*.')) return matchDomainPattern(hostname, pattern);
    return normalizeHostname(hostname) === normalizeHostname(pattern);
}

/**
 * Resolve an anchor against the admin-configured attribution map, in order:
 *   1. `data-ad-id` on the anchor or a nearest ancestor — always wins.
 *   2. Hostname match (non-`tel:` links only) via hostMatchesForBilling()
 *      — EXACT unless the entry uses an explicit `*.` wildcard; see that
 *      function's doc comment for why billing diverges from
 *      matchDomainPattern()'s looser tagging/exclusion semantics.
 *   3. `tel:` match, both sides normalized to their last 10 digits.
 * Returns null when nothing matches — the caller must never beacon.
 *
 * `event_kind` is 'call' for any `tel:` href, 'click' otherwise — independent
 * of which resolution step produced the match.
 *
 * @param {*} anchor An anchor element (real `<a>`, or a test stub exposing
 *   `href`/`hostname`/`dataset`/`parentElement`).
 * @param {Array<{ad_id:number, hosts?:string[], tels?:string[]}>} entries
 *   The sanitized `leadBeaconAttribution` array.
 * @return {?{adId: number, eventKind: 'click'|'call'}}
 */
export function resolveAttribution(anchor, entries) {
    const href = anchor && anchor.href;
    if (typeof href !== 'string' || !href) return null;

    const list = Array.isArray(entries) ? entries : [];
    const isTel = href.toLowerCase().startsWith('tel:');
    const eventKind = isTel ? 'call' : 'click';

    const overrideAdId = findDataAdId(anchor);
    if (overrideAdId !== null) {
        return { adId: overrideAdId, eventKind };
    }

    if (!isTel) {
        const hostname = anchor.hostname;
        if (hostname) {
            for (const entry of list) {
                if (!entry || !(entry.ad_id > 0)) continue;
                const hosts = Array.isArray(entry.hosts) ? entry.hosts : [];
                if (hosts.some((pattern) => hostMatchesForBilling(hostname, pattern))) {
                    return { adId: entry.ad_id, eventKind };
                }
            }
        }
        return null;
    }

    const hrefDigits = normalizeTelDigits(href);
    if (!hrefDigits) return null;

    for (const entry of list) {
        if (!entry || !(entry.ad_id > 0)) continue;
        const tels = Array.isArray(entry.tels) ? entry.tels : [];
        if (tels.some((tel) => normalizeTelDigits(tel) === hrefDigits)) {
            return { adId: entry.ad_id, eventKind };
        }
    }

    return null;
}

/**
 * Build the wire-shape payload go-grad's ingest.go decodes
 * (`beaconBody` in internal/adstore/ingest.go). Exported so
 * tests/lead-beacon.test.mjs drives this REAL function against a
 * fixture-encoded copy of ingest.go's own validation, instead of asserting
 * against a hand-built duplicate object.
 *
 * Guards `crypto.randomUUID` availability INTERNALLY (pr-review-council LOW
 * #14, re-gate round 2) — returns null rather than throwing when it's
 * missing. `fireLeadBeacon()` below already checks this before calling in,
 * but a function that can only be called safely by ONE caller obeying an
 * external precondition is a landmine for the next caller (this IS
 * exported for tests, after all); the internal guard makes "never throws"
 * a real, self-contained property of the function itself.
 *
 * @param {string} dailyId Already-validated 64-hex daily_id.
 * @param {number} adId
 * @param {'click'|'call'} eventKind
 * @return {?{source:string, source_event_id:string, daily_id:string, ad_id:number, event_kind:string}}
 */
export function buildBeaconPayload(dailyId, adId, eventKind) {
    if (typeof crypto === 'undefined' || typeof crypto.randomUUID !== 'function') return null;

    return {
        source: LEAD_BEACON_SOURCE,
        source_event_id: crypto.randomUUID(),
        daily_id: dailyId,
        ad_id: adId,
        event_kind: eventKind,
    };
}

/**
 * Fire the beacon. Debug mode uses fetch+keepalive so the response status
 * is readable and non-204 responses are surfaced (boundaries F1 drift
 * visibility) — production uses `navigator.sendBeacon` (survives
 * navigation, e.g. a `tel:` tap that immediately hands off to the dialer),
 * falling back to fetch+keepalive, fire-and-forget, when `sendBeacon` is
 * unavailable OR throws OR declines to queue. Ported idiom from
 * piter-now's interactions-client.ts `logView()`/`logShare()`.
 *
 * `debugMode` here is ALREADY admin-gated before it ever reaches this
 * module (pr-review-council LOW #10, re-gate round 2, confirmed not a bug):
 * Frontend/Scripts.php computes `'debugMode' => (bool) ($is_admin_user &&
 * $options['debug_mode'])` server-side — a non-admin visitor's browser
 * NEVER receives `debugMode: true` at all, so the console.log/warn calls
 * below are unreachable for them regardless of anything client-side. The
 * `settings.debugMode` this function's caller passes through is that same
 * pre-gated value, not a client-side re-derivation.
 *
 * No-ops (never throws, never beacons) when no CSPRNG is available at all,
 * or when a same-day daily_id could not be resolved.
 *
 * @param {number} adId
 * @param {'click'|'call'} eventKind
 * @param {boolean} debugMode
 */
function fireLeadBeacon(adId, eventKind, debugMode) {
    const dailyId = getDailyId();
    if (!dailyId || !DAILY_ID_RE.test(dailyId)) return;

    const payload = buildBeaconPayload(dailyId, adId, eventKind);
    if (!payload) return;

    const body = JSON.stringify(payload);

    if (debugMode) {
        if (typeof fetch !== 'function') return;

        fetch(LEAD_BEACON_ENDPOINT, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body,
            keepalive: true,
        })
            .then((response) => {
                console.log(`beacon ad_id=${adId} kind=${eventKind} → ${response.status}`);
                if (response.status !== 204) {
                    console.warn(`UTM Linker Lead Beacon: unexpected response ${response.status} for ad_id=${adId}`);
                }
            })
            .catch((error) => {
                console.warn('UTM Linker Lead Beacon: request failed', error);
            });
        return;
    }

    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
        // sendBeacon() can both RETURN false (the UA's own queue is full —
        // pr-review-council HIGH #8) and THROW (pr-review-council LOW #9,
        // re-gate round 2 — e.g. a Blob the UA refuses, or a browser
        // extension/policy intercepting the call) — neither means the
        // beacon was queued. Both fall through to the fetch/keepalive
        // fallback below instead of the fire being silently dropped.
        let queued = false;
        try {
            queued = navigator.sendBeacon(LEAD_BEACON_ENDPOINT, new Blob([body], { type: 'application/json' }));
        } catch (e) {
            queued = false;
        }
        if (queued) return;
    }

    if (typeof fetch === 'function') {
        fetch(LEAD_BEACON_ENDPOINT, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body,
            keepalive: true,
        }).catch(() => {});
    }
}

/**
 * Handle one delegated click/auxclick event: resolve the nearest anchor,
 * dedupe within the current gesture, resolve attribution, and fire.
 * A single malformed link/anchor must never break the rest of the page's
 * click handling — isolate this handler's own errors (same isolation
 * principle as UtmLinker.js#processLinks()'s per-link try/catch).
 *
 * @param {*} event
 * @param {Object} settings Merged plugin settings (defaultSettings.js).
 */
function handleLeadBeaconEvent(event, settings) {
    const anchor = findAncestorAnchor(event && event.target);
    if (!anchor) return;
    if (hasGestureFired(anchor)) return;

    const resolved = resolveAttribution(anchor, settings.leadBeaconAttribution);
    if (!resolved) return;

    markGestureFired(anchor);
    fireLeadBeacon(resolved.adId, resolved.eventKind, settings.debugMode);
}

/**
 * Bind the ONE delegated 'click'+'auxclick' listener on `document` — called
 * once, at UtmLinker construction time, only when `settings.leadBeacon` is
 * on. Never called for the OFF path: no listener is ever attached, so
 * `document.addEventListener` is never invoked and no beacon can ever fire —
 * the OFF path is byte-identical to a build with this module absent
 * entirely.
 *
 * @param {Object} settings Merged plugin settings (defaultSettings.js).
 */
export function attachLeadBeaconListener(settings) {
    if (typeof document === 'undefined' || typeof document.addEventListener !== 'function') return;

    const handler = (event) => {
        try {
            handleLeadBeaconEvent(event, settings);
        } catch (e) {
            if (settings && settings.debugMode) {
                console.error('UTM Linker Lead Beacon: unexpected error', e);
            }
        }
    };

    document.addEventListener('click', handler, { passive: true });
    document.addEventListener('auxclick', handler, { passive: true });
}
